import { Heart, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useUserPreferences } from '@/contexts/UserPreferencesContext';

interface BarNameRowProps {
  barId: string;
  name: string;
  onDisintegrate?: () => void;
}

export function BarNameRow({ barId, name, onDisintegrate }: BarNameRowProps) {
  const navigate = useNavigate();
  const { isFavorite, isHidden, toggleFavorite, toggleHidden } = useUserPreferences();
  const barIsFavorite = isFavorite(barId);
  const barIsHidden = isHidden(barId);

  const handleToggleHidden = () => {
    toggleHidden(barId);
    if (barIsHidden) {
      // Unhiding - just show toast
      toast.success('Bar unhidden');
    } else {
      // Hiding - trigger disintegration if available, otherwise navigate
      if (onDisintegrate) {
        onDisintegrate();
      } else {
        toast.success('Bar hidden');
        navigate(-1);
      }
    }
  };

  return (
    <div className="flex items-center justify-between gap-4">
      <h1 className="text-2xl font-bold truncate">{name}</h1>
      <div className="flex items-center gap-3">
        <button
          onClick={handleToggleHidden}
          className="p-2 -m-2 transition-colors hover:text-[var(--color-muted)]"
          aria-label={barIsHidden ? 'Unhide this bar' : 'Hide this bar'}
        >
          {barIsHidden ? (
            <Eye className="h-6 w-6 text-accent-primary" />
          ) : (
            <EyeOff className="h-6 w-6 text-[var(--color-muted)]" />
          )}
        </button>
        <button
          onClick={() => toggleFavorite(barId)}
          className="p-2 -m-2 transition-colors hover:text-accent-primary"
          aria-label={barIsFavorite ? 'Remove from favorites' : 'Add to favorites'}
        >
          <Heart
            className={`h-6 w-6 ${barIsFavorite ? 'fill-accent-primary text-accent-primary' : ''}`}
          />
        </button>
      </div>
    </div>
  );
}
